"use client";

import { c as _c } from "react/compiler-runtime";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/radix/tooltip";
import { ChevronLeft, ChevronRight } from "lucide-react";
interface CarouselNavigationProps {
  currentIndex: number;
  totalItems: number;
  onPrevious: () => void;
  onNext: () => void;
  onGoToIndex: (index: number) => void;
  disabled?: boolean;
  maxVisibleDots?: number; // Maximum number of dots to show before tapering
}
export function CarouselNavigation(t0) {
  const $ = _c(27);
  const {
    currentIndex,
    totalItems,
    onPrevious,
    onNext,
    onGoToIndex,
    disabled: t1,
    maxVisibleDots: t2
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const maxVisibleDots = t2 === undefined ? 7 : t2;
  if (totalItems <= 1) {
    return null;
  }
  let t3;
  if ($[0] !== currentIndex || $[1] !== maxVisibleDots || $[2] !== totalItems) {
    const getDotProperties = () => {
      if (totalItems <= maxVisibleDots) {
        return Array.from({
          length: totalItems
        }, _temp);
      }
      const dots = [];
      const sideCount = Math.floor((maxVisibleDots - 1) / 2);
      let startIndex = Math.max(0, currentIndex - sideCount);
      let endIndex = Math.min(totalItems - 1, currentIndex + sideCount);
      if (currentIndex < sideCount) {
        endIndex = Math.min(totalItems - 1, maxVisibleDots - 1);
      } else {
        if (currentIndex > totalItems - sideCount - 1) {
          startIndex = Math.max(0, totalItems - maxVisibleDots);
        }
      }
      for (let i = startIndex; i <= endIndex; i++) {
        const distanceFromCurrent = Math.abs(i - currentIndex);
        const maxDistance = Math.max(currentIndex - startIndex, endIndex - currentIndex);
        let opacity = 1;
        let size = "w-2 h-2";
        if (maxDistance > 0) {
          const taperFactor = 1 - distanceFromCurrent / (maxDistance + 1);
          opacity = Math.max(0.3, taperFactor);
          if (distanceFromCurrent > 2) {
            size = "w-1.5 h-1.5";
          } else {
            if (distanceFromCurrent > 1) {
              size = "w-1.5 h-1.5";
            }
          }
        }
        dots.push({
          index: i,
          opacity,
          size,
          isVisible: true
        });
      }
      if (startIndex > 0) {
        dots.unshift({
          index: -1,
          opacity: 0.3,
          size: "w-1 h-1",
          isVisible: true
        });
      }
      if (endIndex < totalItems - 1) {
        dots.push({
          index: -2,
          opacity: 0.3,
          size: "w-1 h-1",
          isVisible: true
        });
      }
      return dots;
    };
    t3 = getDotProperties();
    $[0] = currentIndex;
    $[1] = maxVisibleDots;
    $[2] = totalItems;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const dotProperties = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ChevronLeft className="w-4 h-4" aria-hidden="true" />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== disabled || $[6] !== onPrevious) {
    t5 = <TooltipTrigger asChild={true}><button onClick={onPrevious} type="button" aria-label="Previous" className="p-2 hover:bg-gray-200 rounded-full cursor-pointer transition-colors" disabled={disabled}>{t4}</button></TooltipTrigger>;
    $[5] = disabled;
    $[6] = onPrevious;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <TooltipContent><p>Previous</p></TooltipContent>;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5) {
    t7 = <Tooltip>{t5}{t6}</Tooltip>;
    $[9] = t5;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== currentIndex || $[12] !== disabled || $[13] !== dotProperties || $[14] !== onGoToIndex) {
    t8 = <div className="flex flex-nowrap flex-1 mx-4 justify-center"><div className="flex flex-nowrap gap-1 items-center">{dotProperties.map((dot, _arrayIndex) => <button type="button" title={`Go to item ${dot.index + 1}`} aria-label={`Go to item ${dot.index + 1}`} key={dot.index} onClick={() => onGoToIndex(dot.index)} className={`${dot.size} rounded-full transition-all duration-200 ${dot.index === currentIndex ? "bg-primary" : "bg-gray-300"}`} style={{
          opacity: dot.opacity
        }} disabled={disabled} />)}</div></div>;
    $[11] = currentIndex;
    $[12] = disabled;
    $[13] = dotProperties;
    $[14] = onGoToIndex;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ChevronRight className="w-4 h-4" aria-hidden="true" />;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== disabled || $[18] !== onNext) {
    t10 = <TooltipTrigger asChild={true}><button type="button" aria-label="Next" onClick={onNext} className="p-2 hover:bg-gray-200 rounded-full cursor-pointer transition-colors" disabled={disabled}>{t9}</button></TooltipTrigger>;
    $[17] = disabled;
    $[18] = onNext;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <TooltipContent><p>Next</p></TooltipContent>;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t10) {
    t12 = <Tooltip>{t10}{t11}</Tooltip>;
    $[21] = t10;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t12 || $[24] !== t7 || $[25] !== t8) {
    t13 = <div className="flex items-center justify-between px-2 py-2 bg-gray-50 border-b">{t7}{t8}{t12}</div>;
    $[23] = t12;
    $[24] = t7;
    $[25] = t8;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  return t13;
}
function _temp(_, index) {
  return {
    index,
    opacity: 1,
    size: "w-2 h-2",
    isVisible: true
  };
}